import { useEffect, useState } from "react";
import axiosInstance from "../../../api/axiosInstance";
import {
  TEXT_RULES,
  ITEM_RULES,
  REPEATER_RULES,
  IMAGE_SPECS,
  validateText,
  validateUrl,
  validateByRules,
  validateRepeaterCount,
  validateImageBeforeUpload,
} from "./validation";
import Editor from "../../../components/RichTextEditor/Editor"

const API = "/api/superadmin/supplier-page-master/";

/* ------------------------------------------------------------------ */
/* Defaults / merge                                                    */
/* ------------------------------------------------------------------ */
const defaults = {
  hero: {
    badge: "AI Powered Platform For Suppliers",
    heading: "Win More RFQs. Connect with Better Buyers Faster Payment",
    subheading:
      "Join verified suppliers on Procman to access new business opportunities, win more RFQs, receive timely payments, and grow confidently through a secure, transparent, and efficient procurement.",
    primary_cta_label: "Join as Supplier for free",
    primary_cta_url: "/login",
    secondary_cta_label: "Explore Worldwide RFQs",
    secondary_cta_url: "/public-rfqs",
  },

  metrics: {
    items: [
      { number: "50M+", label: "Business won by supplier" },
      { number: "10,000+", label: "Verified Buyers" },
      { number: "500M+", label: "Spend Managed" },
      { number: "25,000+", label: "RFQs Published Every Month" },
      { number: "98%", label: "On-Time Payments" },
      { number: "24/7", label: "Supplier Support" },
    ],
  },

  benefits: {
    heading: "Why Suppliers Love Procman",
    items: [
      {
        title: "More RFQs",
        description: "Get matched with relevant RFQs every day.",
      },
      {
        title: "Verified Buyers",
        description: "Only engage with verified buyers on our platform.",
      },
      {
        title: "Easy & Free",
        description: "100% free to join and easy to get started.",
      },
      {
        title: "Secure Payments",
        description:
          "Protection against delayed payments using secure workflows.",
      },
      {
        title: "Instant Tracking",
        description: "Get paid on time with transparent tracking.",
      },
      {
        title: "Business Growth",
        description: "Grow your business with repeat opportunities.",
      },
    ],
  },

  features: {
    heading: "Powerful Features to Help You Win and Grow",
    items: [
      {
        title: "AI Powered RFQ Marketplace",
        description:
          "Get high intent RFQs matched to your products and services.",
      },
      {
        title: "Smart Quote Management",
        description:
          "Create, submit and manage quotes all in one place.",
      },
      {
        title: "Order & Delivery Tracking",
        description:
          "Track orders, shipping, PO and delivery status from one dashboard.",
      },
      {
        title: "Secure Payment Tracking",
        description:
          "Get paid on time with full payment visibility.",
      },
      {
        title: "Performance Analytics",
        description:
          "Track win rates, response times and business growth.",
      },
    ],
    cta_label: "Explore All Features",
    cta_url: "/features",
  },

  working: {
    heading: "How Procman Works",
    subheading:
      "Get opportunities that match your products and capabilities.",
    steps: [
      {
        title: "Access Verified Buyers",
        description:
          "Connect with trusted buyers actively sourcing products and services.",
      },
      {
        title: "Get Matching RFQs",
        description:
          "Get opportunities that match your products and capabilities.",
      },
      {
        title: "Submit Quotations",
        description:
          "Send competitive quotes digitally through a standardized process.",
      },
      {
        title: "Shortlisting & Award",
        description:
          "Participate in transparent evaluations and improve your win rate.",
      },
      {
        title: "Delivery Management",
        description:
          "Track order fulfillment and provide delivery updates seamlessly.",
      },
      {
        title: "Payment Tracking",
        description:
          "Monitor payment progress and ensure smoother payment cycles.",
      },
    ],
  },

  industries: {
    heading: "Procurement solution for every industries",
  },

  comparison: {
    heading: "Why Procman Beats Traditional Procurement",
    subheading:
      "Upgrade from manual friction to automated growth with intelligence.",
    left_title: "Traditional Procurement",
    right_title: "Procman Platform",
    left_items: [
      "Limited visibility of opportunities",
      "Manual follow-ups & long cycle",
      "Unclear requirements",
      "Late payments & delays",
      "No performance visibility",
    ],
    right_items: [
      "AI matches high intent RFQs",
      "Real-time notifications & tracking",
      "Clear requirements & documents",
      "100% secure & on-time payments",
      "Performance score & business insights",
    ],
  },

  banner: {},

  case_studies: {
    heading: "",
    subheading: "",
  },

  testimonials: {
    heading: "",
    subheading: "",
  },

  faqs: [],

  cta: {
    heading: "Ready to Transform Your Procurement?",
    subheading:
      "Join hundreds of companies using ProcMan to simplify procurement and drive better business outcomes.",
    primary_cta_label: "Join as Supplier for free",
    primary_cta_url: "/login",
    secondary_cta_label: "Book Demo",
    secondary_cta_url: "/book-demo",
    benefits: [
      { text: " No Credit Card Required" },
      { text: "Free 30-Day Trial" },
      { text: "Setup in Minutes" },
    ],
  },

  seo: {
    meta_title: "Supplier procurement Automation",
    meta_description:
      "Join thousands of verified suppliers on Procman to discover new business opportunities, win more contracts, receive payments on time, and scale your business with confidence through a transparent and efficient procurement network.",
    meta_keywords: "",
    canonical_url: "https://procman.ai/supplier",
    og_title: "",
    og_description: "",
    twitter_title: "",
    twitter_description: "",
    "robots_index": true,
    seo_content: "",
  },

  schema: {
    "enable_faq": true,
    "enable_webpage": true,
    custom_json: "",
    additional_schema_type: "",
    additional_schema_data: {},
  },

  hero_desktop_image: null,
  hero_mobile_image: null,
  og_image: null,
  twitter_image: null,
};

const OBJECT_FIELDS = [
  "hero",
  "metrics",
  "benefits",
  "features",
  "working",
  "industries",
  "comparison",
  "banner",
  "case_studies",
  "testimonials",
  "cta",
  "seo",
];
const IMAGE_FIELDS = ["hero_desktop_image", "hero_mobile_image", "og_image", "twitter_image"];
// faqs is a top-level array in this API, schema is handled separately
const CONTENT_FIELDS = [...OBJECT_FIELDS, "faqs", "schema"];

const arr = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});



const isBlank = (v) =>
  v === undefined || v === null || (typeof v === "string" && v.trim() === "");

const hasContent = (item) =>
  typeof item === "string"
    ? !isBlank(item)
    : Object.values(obj(item)).some((v) => !isBlank(v));

const listOrDefault = (apiList, defaultList) => {
  const list = arr(apiList);
  return list.some(hasContent) ? list : defaultList;
};

const mergeFilled = (base, incoming) => {
  const out = { ...base };
  Object.entries(obj(incoming)).forEach(([k, v]) => {
    if (!isBlank(v)) out[k] = v;
  });
  return out;
};

const merge = (data = {}) => {
  const out = { ...defaults };
  OBJECT_FIELDS.forEach((key) => {
    out[key] = mergeFilled(defaults[key], data[key]);
  });
  out.metrics.items = listOrDefault(data.metrics?.items, defaults.metrics.items);
  out.benefits.items = listOrDefault(data.benefits?.items, defaults.benefits.items);
  out.features.items = listOrDefault(data.features?.items, defaults.features.items);
  out.working.steps = listOrDefault(data.working?.steps, defaults.working.steps);
  out.comparison.left_items = listOrDefault(data.comparison?.left_items, defaults.comparison.left_items);
  out.comparison.right_items = listOrDefault(data.comparison?.right_items, defaults.comparison.right_items);
  out.cta.benefits = listOrDefault(data.cta?.benefits, defaults.cta.benefits);
  out.faqs = arr(data.faqs);
  out.schema = {
    ...defaults.schema,
    ...obj(data.schema),
    additional_schema_data: obj(data.schema?.additional_schema_data),
  };
  out.seo.canonical_url =
    data.seo?.canonical_url?.trim() || defaults.seo.canonical_url;
  out.seo.og_title = out.seo.og_title || out.seo.meta_title || "";
  out.seo.og_description = out.seo.og_description || out.seo.meta_description || "";
  out.seo.twitter_title = out.seo.twitter_title || out.seo.og_title || "";
  out.seo.twitter_description = out.seo.twitter_description || out.seo.og_description || "";

  IMAGE_FIELDS.forEach((key) => {
    out[key] = data[key] ?? null;
  });
  const ri = data.seo?.robots_index;
  out.seo.robots_index =
    ri === "" || ri === undefined || ri === null
      ? defaults.seo.robots_index            // empty => default true
      : ri === true || String(ri).toLowerCase() === "true" || ri === "index, follow";

  return out;
};

/* ------------------------------------------------------------------ */
/* UI primitives                                                       */
/* ------------------------------------------------------------------ */
const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 14px",
  border: "1.5px solid #cbd5e1",
  borderRadius: 8,
  marginTop: 6,
  fontSize: 14,
  fontWeight: 400,
  color: "#0f172a",
  backgroundColor: "#ffffff",
  transition: "all 0.2s ease-in-out",
};

const formatFieldLabel = (key) =>
  key
    .split("_")
    .map((word) => {
      const acronyms = { cta: "CTA", url: "URL", seo: "SEO", id: "ID", json: "JSON", ld: "LD", og: "OG", faqs: "FAQs" };
      return acronyms[word] || `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    })
    .join(" ");

const setErrorEntry = (setErrors, key, message) =>
  setErrors((prev) => {
    if (prev[key] === message) return prev;
    const next = { ...prev };
    if (message) next[key] = message;
    else delete next[key];
    return next;
  });

const Section = ({ title, description, children }) => (
  <section
    style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      padding: "26px 28px",
      marginBottom: 28,
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03), 0 2px 4px -2px rgba(0,0,0,0.03)",
    }}
  >
    <div style={{ borderBottom: "2px solid #f1f5f9", paddingBottom: 12, marginBottom: 20 }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em" }}>{title}</h2>
      {description && (
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "#64748b", lineHeight: 1.6, fontWeight: 400 }}>{description}</p>
      )}
    </div>
    <div style={{ display: "grid", gap: 12 }}>{children}</div>
  </section>
);

const SubBlock = ({ title, description, headerAction, children }) => (
  <div className="subblock">
    {(title || headerAction) && (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
        {title && (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#334155",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              background: "#f1f5f9",
              padding: "4px 10px",
              borderRadius: 6,
              border: "1px solid #e2e8f0",
              display: "inline-block",
            }}
          >
            {title}
          </div>
        )}
        {headerAction && <div>{headerAction}</div>}
      </div>
    )}
    {description && <p style={{ margin: "-4px 0 14px", fontSize: 13, color: "#64748b", lineHeight: 1.5 }}>{description}</p>}
    {children}
  </div>
);

const Field = ({ label, value, onChange, textarea, type = "text", readOnly = false, limits, error }) => {
  const len = (value || "").toString().trim().length;
  const showCount = Boolean(limits?.max);
  const invalid = Boolean(error);
  const fieldStyle = { ...input, ...(invalid ? { borderColor: "#e11d48", background: "#fff1f2" } : {}) };
  return (
    <label style={{ display: "block", marginBottom: 16, fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span>
          {label}
          {limits?.required && <span style={{ color: "#e11d48" }}> *</span>}
        </span>
        {showCount && (
          <span style={{ fontSize: 11, fontWeight: 600, color: invalid ? "#e11d48" : "#64748b", whiteSpace: "nowrap" }}>
            {len}/{limits.max}
          </span>
        )}
      </div>
      {textarea ? (
        <textarea rows={3} style={fieldStyle} value={value || ""} readOnly={readOnly} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} style={fieldStyle} value={value || ""} readOnly={readOnly} onChange={(e) => onChange(e.target.value)} />
      )}
      {limits?.help && !error && (
        <div style={{ marginTop: 5, fontSize: 12, color: "#64748b", fontWeight: 400, lineHeight: 1.4 }}>{limits.help}</div>
      )}
      {error && <div style={{ marginTop: 5, fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{error}</div>}
    </label>
  );
};

function Repeater({ title, description, items, fields, fieldRules = {}, repeaterRules, path, errors, setErrors, onChange }) {
  const countError = repeaterRules ? validateRepeaterCount(items, repeaterRules) : null;

  useEffect(() => {
    setErrorEntry(setErrors, path, countError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countError, path]);

  const atMax = Boolean(repeaterRules?.max && items.length >= repeaterRules.max);
  const atMin = Boolean(repeaterRules?.min && items.length <= repeaterRules.min);

  const add = () => {
    if (atMax) return;
    onChange([...items, Object.fromEntries(fields.map(([key]) => [key, ""]))]);
  };
  const remove = (index) => {
    if (atMin) return;
    onChange(items.filter((_, i) => i !== index));
  };
  const setFieldValue = (index, key, value) => {
    onChange(items.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
    const message = validateByRules(value, fieldRules[key], formatFieldLabel(key));
    setErrorEntry(setErrors, `${path}[${index}].${key}`, message);
  };

  const inlineFields = fields.filter(([, textarea]) => !textarea);
  const blockFields = fields.filter(([, textarea]) => textarea);
  const nounLabel = repeaterRules?.noun || "item";

  const addBtn = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {repeaterRules && (
        <span style={{ fontSize: 12, fontWeight: 600, color: countError ? "#e11d48" : "#64748b" }}>
          {items.length}
          {repeaterRules.max ? `/${repeaterRules.max}` : ""} {nounLabel}s{repeaterRules.min ? ` · min ${repeaterRules.min}` : ""}
        </span>
      )}
      <button
        type="button"
        className="primary"
        onClick={add}
        disabled={atMax}
        style={{ opacity: atMax ? 0.5 : 1, cursor: atMax ? "not-allowed" : "pointer", margin: 0 }}
      >
        + Add {nounLabel}
      </button>
    </div>
  );

  return (
    <SubBlock title={title} description={description} headerAction={addBtn}>
      {countError && <div style={{ marginBottom: 12, fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{countError}</div>}
      <div style={{ display: "grid", gap: 12 }}>
        {items.map((item, index) => (
          <div
            key={index}
            style={{ border: "1px solid #cbd5e1", borderRadius: 10, background: "#ffffff", padding: "16px", boxShadow: "0 1px 2px rgba(0,0,0,0.03)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "#e0e7ff",
                  color: "#4f46e5",
                  fontSize: 13,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {index + 1}
              </span>
              <button
                type="button"
                onClick={() => remove(index)}
                disabled={atMin}
                style={{
                  margin: 0,
                  borderColor: "#fecaca",
                  background: "#fff1f2",
                  color: "#e11d48",
                  opacity: atMin ? 0.4 : 1,
                  cursor: atMin ? "not-allowed" : "pointer",
                }}
              >
                Remove
              </button>
            </div>
            {inlineFields.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${inlineFields.length}, minmax(140px, 1fr))`, gap: 12 }}>
                {inlineFields.map(([key]) => (
                  <Field
                    key={key}
                    label={formatFieldLabel(key)}
                    value={item[key]}
                    limits={fieldRules[key]}
                    error={errors?.[`${path}[${index}].${key}`]}
                    onChange={(value) => setFieldValue(index, key, value)}
                  />
                ))}
              </div>
            )}
            {blockFields.map(([key]) => (
              <Field
                key={key}
                label={formatFieldLabel(key)}
                textarea
                value={item[key]}
                limits={fieldRules[key]}
                error={errors?.[`${path}[${index}].${key}`]}
                onChange={(value) => setFieldValue(index, key, value)}
              />
            ))}
          </div>
        ))}
      </div>
      {!items.length && <p style={{ margin: "10px 0 0", fontSize: 13, color: "#64748b" }}>Nothing added yet.</p>}
    </SubBlock>
  );
}

function StringListEditor({ title, description, items, path, rules, repeaterRules, errors, setErrors, onChange, placeholder = "Text" }) {
  const countError = repeaterRules ? validateRepeaterCount(items, repeaterRules) : null;

  useEffect(() => {
    setErrorEntry(setErrors, path, countError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countError, path]);

  const atMax = Boolean(repeaterRules?.max && items.length >= repeaterRules.max);
  const atMin = Boolean(repeaterRules?.min && items.length <= repeaterRules.min);

  const add = () => {
    if (!atMax) onChange([...items, ""]);
  };
  const remove = (index) => {
    if (!atMin) onChange(items.filter((_, i) => i !== index));
  };
  const setValue = (index, value) => {
    onChange(items.map((row, i) => (i === index ? value : row)));
    setErrorEntry(setErrors, `${path}[${index}]`, validateText(value, rules, title));
  };

  const nounLabel = repeaterRules?.noun || "item";

  const addBtn = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {repeaterRules && (
        <span style={{ fontSize: 12, fontWeight: 600, color: countError ? "#e11d48" : "#64748b" }}>
          {items.length}
          {repeaterRules.max ? `/${repeaterRules.max}` : ""} {nounLabel}s
        </span>
      )}
      <button
        type="button"
        className="primary"
        onClick={add}
        disabled={atMax}
        style={{ opacity: atMax ? 0.5 : 1, cursor: atMax ? "not-allowed" : "pointer", margin: 0 }}
      >
        + Add {nounLabel}
      </button>
    </div>
  );

  return (
    <SubBlock title={title} description={description} headerAction={addBtn}>
      {countError && <div style={{ marginBottom: 12, fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{countError}</div>}
      {items.map((item, index) => {
        const error = errors?.[`${path}[${index}]`];
        return (
          <div key={index} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                style={{ ...input, marginTop: 0, ...(error ? { borderColor: "#e11d48", background: "#fff1f2" } : {}) }}
                value={item}
                placeholder={placeholder}
                onChange={(e) => setValue(index, e.target.value)}
              />
              <button
                type="button"
                onClick={() => remove(index)}
                disabled={atMin}
                style={{ opacity: atMin ? 0.4 : 1, borderColor: "#fecaca", background: "#fff1f2", color: "#e11d48", height: "42px", margin: 0 }}
              >
                Remove
              </button>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              {error ? <span style={{ fontSize: 12, color: "#e11d48", fontWeight: 600 }}>{error}</span> : <span />}
              {rules?.max && (
                <span style={{ fontSize: 11, color: "#64748b" }}>
                  {(item || "").length}/{rules.max}
                </span>
              )}
            </div>
          </div>
        );
      })}
      {!items.length && <p style={{ margin: "10px 0 0", fontSize: 13, color: "#64748b" }}>Nothing added yet.</p>}
    </SubBlock>
  );
}

function ImageUploadField({ label, fieldName, value, fallbackValue, fallbackLabel, disabled, uploading, onUpload, onPreview, error, setError }) {
  const [localPreview, setLocalPreview] = useState(null);
  const spec = IMAGE_SPECS?.[fieldName];
  const isCustom = Boolean(localPreview || value);
  const previewUrl = localPreview || value || fallbackValue;

  const handleFile = async (file) => {
    if (!file) return;
    if (spec) {
      const problems = await validateImageBeforeUpload(file, fieldName);
      if (problems.length) {
        setError?.(problems.join(" "));
        return;
      }
    }
    setError?.(null);
    setLocalPreview(URL.createObjectURL(file));
    onUpload(fieldName, file);
  };

  return (
    <div style={{ marginBottom: 18, background: "#f8fafc", padding: "14px", borderRadius: 10, border: "1px solid #e2e8f0" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 16, alignItems: "center" }}>
        <div
          style={{
            height: 80,
            width: 130,
            borderRadius: 8,
            border: `1.5px dashed ${error ? "#fca5a5" : "#cbd5e1"}`,
            background: "#ffffff",
            overflow: "hidden",
            display: "grid",
            placeItems: "center",
          }}
        >
          {previewUrl ? (
            <button
              type="button"
              aria-label={`Preview ${label}`}
              onClick={() => onPreview && onPreview(previewUrl)}
              style={{ padding: 0, margin: 0, border: 0, background: "none", width: "100%", height: "100%", cursor: "zoom-in" }}
            >
              <img src={previewUrl} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </button>
          ) : (
            <span style={{ color: "#64748b", fontSize: 11, textAlign: "center", padding: 6, fontWeight: 500 }}>No image</span>
          )}
        </div>
        <div>
          <input
            id={`img-${fieldName}`}
            type="file"
            accept={spec ? spec.formats.join(",") : "image/png,image/jpeg,image/webp"}
            disabled={disabled || uploading}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              handleFile(file);
            }}
            style={{ display: "none" }}
          />
          <label
            htmlFor={`img-${fieldName}`}
            style={{
              display: "inline-block",
              border: "1px solid #bfdbfe",
              borderRadius: 6,
              padding: "8px 14px",
              background: "#eff6ff",
              color: "#2563eb",
              fontSize: 13,
              fontWeight: 700,
              cursor: disabled || uploading ? "not-allowed" : "pointer",
              opacity: disabled || uploading ? 0.55 : 1,
              transition: "all 0.2s ease",
            }}
          >
            {uploading ? "Uploading…" : isCustom ? "Replace image" : "Upload image"}
          </label>
          {spec?.help && !error && <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 12, lineHeight: 1.4 }}>{spec.help}</p>}
          {disabled && (
            <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 12, lineHeight: 1.4 }}>Save the supplier page once before uploading images.</p>
          )}
          {error && <p style={{ margin: "8px 0 0", color: "#e11d48", fontSize: 12, fontWeight: 600 }}>{error}</p>}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Additional schema generator (same as HomePageMaster)                */
/* ------------------------------------------------------------------ */
const ADDITIONAL_SCHEMA_FIELDS = {
  Organization: [
    ["name", "Organization name"],
    ["url", "Organization URL"],
    ["description", "Organization description", true],
    ["logo", "Logo URL"],
  ],
  Service: [
    ["name", "Service name"],
    ["description", "Service description", true],
    ["url", "Service URL"],
    ["provider_name", "Provider name"],
  ],
  SoftwareApplication: [
    ["name", "Application name"],
    ["description", "Application description", true],
    ["url", "Application URL"],
    ["application_category", "Application category"],
    ["operating_system", "Operating system"],
  ],
};

const buildAdditionalSchema = (type, values = {}) => {
  const value = (key, fallback = "") => values[key]?.trim() || fallback;
  switch (type) {
    case "Organization":
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: value("name"),
        url: value("url"),
        description: value("description"),
        logo: value("logo"),
      };
    case "Service":
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        name: value("name"),
        description: value("description"),
        url: value("url"),
        provider: { "@type": "Organization", name: value("provider_name", "Procman") },
      };
    case "SoftwareApplication":
      return {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: value("name"),
        description: value("description"),
        url: value("url"),
        applicationCategory: value("application_category", "BusinessApplication"),
        operatingSystem: value("operating_system", "Web"),
      };
    default:
      return null;
  }
};

function AdditionalSchemaGenerator({ schema, onChange }) {
  const [preview, setPreview] = useState(null);
  const selectedType = schema.additional_schema_type || "";
  const values = schema.additional_schema_data || {};
  const fields = ADDITIONAL_SCHEMA_FIELDS[selectedType] || [];
  const updateValue = (key, value) => onChange({ ...schema, additional_schema_data: { ...values, [key]: value } });

  return (
    <SubBlock title="Generate an additional schema">
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#1e293b", marginBottom: 12 }}>
        Schema type
        <select
          style={input}
          value={selectedType}
          onChange={(e) => {
            onChange({ ...schema, additional_schema_type: e.target.value, additional_schema_data: {} });
            setPreview(null);
          }}
        >
          <option value="">None</option>
          <option value="Organization">Organization</option>
          <option value="Service">Service</option>
          <option value="SoftwareApplication">Software Application</option>
        </select>
      </label>
      {fields.map(([key, label, textarea]) => (
        <Field key={key} label={label} value={values[key]} textarea={textarea} onChange={(value) => updateValue(key, value)} />
      ))}
      {selectedType && (
        <>
          <button type="button" className="primary" onClick={() => setPreview(buildAdditionalSchema(selectedType, values))} style={{ marginTop: 8 }}>
            Generate schema
          </button>
          {preview && (
            <pre
              style={{
                margin: "14px 0 0",
                padding: 14,
                borderRadius: 8,
                background: "#0f172a",
                color: "#e2e8f0",
                fontSize: 12,
                lineHeight: 1.5,
                overflowX: "auto",
              }}
            >
              {JSON.stringify(preview, null, 2)}
            </pre>
          )}
        </>
      )}
    </SubBlock>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function SupplierPageMaster() {
  const [record, setRecord] = useState(null);
  const [content, setContent] = useState(merge());
  const [notice, setNotice] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImages, setUploadingImages] = useState({});
  const [overlayUrl, setOverlayUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    axiosInstance
      .get(API)
      .then(({ data }) => {
        const row = data?.data?.[0];
        if (row) {
          setRecord(row);
          setContent(merge(row));
        }
      })
      .catch(() => setNotice("Could not load Supplier Page Master."))
      .finally(() => setLoaded(true));
  }, []);

  // const setGroup = (group, key, value) => setContent((state) => ({ ...state, [group]: { ...state[group], [key]: value } }));

  const setGroup = (group, key, value) =>
    setContent((state) => {
      const nextGroup = { ...state[group], [key]: value };

      // Meta title/description change hone par OG + Twitter auto-sync
      if (group === "seo" && key === "meta_title") {
        nextGroup.og_title = value;
        nextGroup.twitter_title = value;
      }
      if (group === "seo" && key === "meta_description") {
        nextGroup.og_description = value;
        nextGroup.twitter_description = value;
      }

      return { ...state, [group]: nextGroup };
    });
  const setField = (group, value) => setContent((state) => ({ ...state, [group]: value }));

  // Text/URL field bound to content[group][key] with validation
  const bind = (group, key, label, { url = false } = {}) => {
    const rulesKey = `${group}.${key}`;
    const rules = TEXT_RULES[rulesKey];
    return {
      label,
      value: content[group][key],
      limits: rules,
      error: errors[rulesKey],
      onChange: (v) => {
        setGroup(group, key, v);
        setErrorEntry(setErrors, rulesKey, url ? validateUrl(v, true, label) : validateText(v, rules, label));
      },
    };
  };

  const hasBlockingErrors = Object.keys(errors).length > 0;

  const refresh = async (id) => {
    const targetId = id || record?.id;
    if (!targetId) return;
    const { data } = await axiosInstance.get(`${API}${targetId}/`);
    if (data?.data) {
      setRecord(data.data);
      setContent(merge(data.data));
    }
  };

  const save = async () => {
    if (hasBlockingErrors) {
      setNotice(`Fix ${Object.keys(errors).length} validation error(s) before saving.`);
      return;
    }
    const payload = Object.fromEntries(CONTENT_FIELDS.map((field) => [field, content[field]]));
    setSaving(true);
    try {
      if (record) {
        await axiosInstance.put(`${API}${record.id}/`, payload);
        await refresh(record.id);
      } else {
        // POST works only once; if the record already exists, fall back to PUT
        let targetId;
        try {
          const response = await axiosInstance.post(API, payload);
          targetId = response.data?.id || response.data?.data?.id;
          if (response.data?.success === false) targetId = null;
        } catch (e) {
          targetId = null;
        }
        if (!targetId) {
          const { data } = await axiosInstance.get(API);
          const existing = data?.data?.[0];
          if (existing) {
            targetId = existing.id;
            await axiosInstance.put(`${API}${targetId}/`, payload);
          }
        }
        if (targetId) await refresh(targetId);
      }
      setNotice("Supplier page saved successfully.");
    } catch (error) {
      const errorsData = error?.response?.data;
      setNotice(
        errorsData?.message || errorsData?.detail || (errorsData ? Object.values(errorsData).flat().join(" ") : "Could not save Supplier page."),
      );
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (fieldName, file) => {
    if (!record) {
      setNotice("Save the supplier page once before uploading images.");
      return;
    }
    setUploadingImages((state) => ({ ...state, [fieldName]: true }));
    try {
      const body = new FormData();
      body.append(fieldName, file);
      await axiosInstance.put(`${API}${record.id}/`, body, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh(record.id);
      setNotice(`${formatFieldLabel(fieldName)} uploaded successfully.`);
    } catch (error) {
      setNotice(error?.response?.data?.message || error?.response?.data?.detail || `Could not upload ${formatFieldLabel(fieldName)}.`);
    } finally {
      setUploadingImages((state) => ({ ...state, [fieldName]: false }));
    }
  };

  const imageProps = (fieldName, fallbackValue = null, fallbackLabel = "") => ({
    fieldName,
    value: content[fieldName],
    fallbackValue,
    fallbackLabel,
    disabled: !record,
    uploading: Boolean(uploadingImages[fieldName]),
    onUpload: uploadImage,
    onPreview: setOverlayUrl,
    error: imageErrors[fieldName],
    setError: (message) => setErrorEntry(setImageErrors, fieldName, message),
  });

  const repeaterCommon = { errors, setErrors };

  return (
    <div className="homepage-cms" style={{ minHeight: "100vh", padding: "32px 24px", background: "#f8fafc", fontFamily: "Inter,Arial,sans-serif" }}>
      <style>{`
        .homepage-cms button {
          border: 1px solid #bfdbfe;
          border-radius: 8px;
          background: #eff6ff;
          color: #2563eb;
          padding: 8px 14px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          margin-right: 8px;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .homepage-cms button:hover { background: #dbeafe; border-color: #93c5fd; }
        .homepage-cms .primary {
          border: 0;
          background: #2563eb;
          color: #ffffff;
          padding: 9px 18px;
          font-size: 13px;
          font-weight: 600;
          box-shadow: 0 1px 2px rgba(37,99,235,0.2);
        }
        .homepage-cms .primary:hover { background: #1d4ed8; }
        .homepage-cms input:focus, .homepage-cms textarea:focus, .homepage-cms select:focus {
          outline: none;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.15) !important;
        }
        .homepage-cms .subblock:first-child { border-top: 0; margin-top: 0; padding-top: 0; }
        .homepage-cms .rte-wrapper button {
  all: revert-layer;
  .homepage-cms .rte-wrapper button {
  margin-right: 0;
  padding: revert;
  border: revert;
  background: transparent;
  color: inherit;
  border-radius: revert;
}
.homepage-cms .rte-wrapper button:hover {
  background: revert;
  border-color: revert;
}
}
      `}</style>

      {!loaded ? (
        <div style={{ padding: "60px 0", textAlign: "center", color: "#64748b", fontSize: 14, fontWeight: 600 }}>Loading supplier page content...</div>
      ) : (

        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, marginBottom: 28, background: "#ffffff", padding: "20px 24px", borderRadius: 12, border: "1px solid #e2e8f0" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>Supplier Page</h1>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "#64748b" }}>
                Manage the Procman AI supplier landing page content, images, SEO, and structured data.
              </p>
            </div>
            <div style={{ position: "fixed", top: 88, right: 48, zIndex: 1200, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
              <button
                className="primary"
                disabled={saving || hasBlockingErrors}
                onClick={save}
                title={hasBlockingErrors ? `${Object.keys(errors).length} field(s) need fixing before you can save` : undefined}
                style={{
                  margin: 0,
                  boxShadow: "0 6px 16px rgba(37,99,235,.3)",
                  opacity: hasBlockingErrors ? 0.5 : 1,
                  cursor: hasBlockingErrors ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Saving..." : "Save Data"}
              </button>
              {hasBlockingErrors && (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#e11d48", background: "#fff1f2", border: "1px solid #fecaca", borderRadius: 6, padding: "3px 8px" }}>
                  {Object.keys(errors).length} field(s) need fixing
                </span>
              )}
            </div>
          </div>

          {notice && (
            <div
              style={{
                padding: "12px 16px",
                marginBottom: 20,
                borderRadius: 8,
                fontWeight: 600,
                fontSize: 14,
                background: notice.toLowerCase().includes("success") ? "#f0fdf4" : "#fef2f2",
                border: `1px solid ${notice.toLowerCase().includes("success") ? "#bbf7d0" : "#fecaca"}`,
                color: notice.toLowerCase().includes("success") ? "#166534" : "#b91c1c",
              }}
            >
              {notice}
            </div>
          )}



          {/* SEO */}
          <Section title="SEO">
            <SubBlock title="">
              <Field label="Meta Title" value={content.seo.meta_title} onChange={(v) => setGroup("seo", "meta_title", v)} />
              <Field label="Meta Description" textarea value={content.seo.meta_description} onChange={(v) => setGroup("seo", "meta_description", v)} />
              <Field label="Meta Keywords" value={content.seo.meta_keywords} onChange={(v) => setGroup("seo", "meta_keywords", v)} />
              <Field label="Canonical URL" value={content.seo.canonical_url} onChange={(v) => setGroup("seo", "canonical_url", v)} />
              <Field label="OG Title" value={content.seo.og_title} onChange={(v) => setGroup("seo", "og_title", v)} />
              <Field label="OG Description" textarea value={content.seo.og_description} onChange={(v) => setGroup("seo", "og_description", v)} />
              <Field
                label="Twitter Title"
                value={content.seo.twitter_title}
                onChange={(v) => setGroup("seo", "twitter_title", v)}
              />
              <Field
                label="Twitter Description"
                textarea
                value={content.seo.twitter_description}
                onChange={(v) => setGroup("seo", "twitter_description", v)}
              />
              <label style={{ display: "block", marginBottom: 16, fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                Robots Index
                <select
                  style={input}
                  value={content.seo.robots_index === false ? "noindex" : "index"}
                  onChange={(e) => setGroup("seo", "robots_index", e.target.value === "index")}
                >
                  <option value="index">index, follow</option>
                  <option value="noindex">noindex, nofollow</option>
                </select>
              </label>

            </SubBlock>
            <SubBlock title="SEO Content" description="Long-form SEO content with headings, lists, tables, links, and formatting.">
              {/* <div style={{ marginTop: 12 }}> */}
              {/* <RichTextEditor
                  value={content.seo.seo_content || ""}
                  onChange={(html) => setGroup("seo", "seo_content", html)}
                  placeholder="Write SEO content..."
                  minHeight={300}
                /> */}


              <div className="rte-wrapper" style={{ marginTop: 12 }}>
                <Editor
                  value={content.seo.seo_content || ""}
                  onChange={(html) => setGroup("seo", "seo_content", html)}
                  placeholder="Write SEO content..."
                  minHeight={300}
                />

              </div>
              {/* </div> */}

            </SubBlock>
            <SubBlock title="Images" description="Uploads immediately as soon as a file is chosen.">
              {/* <ImageUploadField label="OG Image" {...imageProps("og_image")} />
              <ImageUploadField label="Twitter Image" {...imageProps("twitter_image")} /> */}
              <ImageUploadField label="OG Image" {...imageProps("og_image", content.hero_desktop_image, "Hero Desktop Image")} />
              <ImageUploadField
                label="Twitter Image"
                {...imageProps(
                  "twitter_image",
                  content.og_image || content.hero_desktop_image,
                  content.og_image ? "OG Image" : "Hero Desktop Image"
                )}
              />

            </SubBlock>
          </Section>

          {/* SCHEMA */}
          <Section title="Schema" description="Structured data (JSON-LD) stored on the supplier page record.">
            <div className=" rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              <p>
                WebPage and Breadcrumb schemas are generated automatically for the supplier page.
              </p>
              <p className="mt-1">
                FAQ schema is generated automatically from every FAQ with a question and answer.
              </p>
            </div>
            <SubBlock title="Custom JSON-LD">
              <Field label="Custom Schema JSON" textarea value={content.schema.custom_json} onChange={(v) => setGroup("schema", "custom_json", v)} />
            </SubBlock>
            <AdditionalSchemaGenerator schema={content.schema} onChange={(schema) => setField("schema", schema)} />
          </Section>


          {/* HERO */}
          <Section title="Hero Section">
            <SubBlock title="Content">
              <Field {...bind("hero", "badge", "Badge")} />
              <Field {...bind("hero", "heading", "Heading")} />
              <Field textarea {...bind("hero", "subheading", "Subheading")} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, background: "#f8fafc", padding: "12px 14px", borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 8 }}>
                <Field {...bind("hero", "primary_cta_label", "Primary CTA Label")} />
                <Field {...bind("hero", "primary_cta_url", "Primary CTA URL", { url: true })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, background: "#f8fafc", padding: "12px 14px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                <Field {...bind("hero", "secondary_cta_label", "Secondary CTA Label")} />
                <Field {...bind("hero", "secondary_cta_url", "Secondary CTA URL", { url: true })} />
              </div>
            </SubBlock>
            <SubBlock title="Images" description="Uploads immediately as soon as a file is chosen.">
              <ImageUploadField label="Hero Desktop Image" {...imageProps("hero_desktop_image")} />
              <ImageUploadField label="Hero Mobile Image" {...imageProps("hero_mobile_image")} />
            </SubBlock>
          </Section>

          {/* METRICS */}
          <Section title="Metrics">
            <Repeater
              title="Metrics"
              items={content.metrics.items}
              fields={[["number"], ["label"]]}
              fieldRules={ITEM_RULES.metric}
              repeaterRules={REPEATER_RULES["metrics.items"]}
              path="metrics.items"
              onChange={(items) => setGroup("metrics", "items", items)}
              {...repeaterCommon}
            />
          </Section>

          {/* BENEFITS */}
          <Section title="Benefits (Why Suppliers Love Procman)">
            <SubBlock title="Content">
              <Field {...bind("benefits", "heading", "Heading")} />
            </SubBlock>
            <Repeater
              title="Benefits"
              items={content.cta.benefits || []}
              fields={[["text"]]}
              fieldRules={{
                text: TEXT_RULES["cta.benefit.text"],
              }}
              repeaterRules={REPEATER_RULES["cta.benefits"]}
              path="cta.benefits"
              errors={errors}
              setErrors={setErrors}
              onChange={(benefits) => setGroup("cta", "benefits", benefits)}
            />
          </Section>

          {/* FEATURES */}
          <Section title="Features">
            <SubBlock title="Content">
              <Field {...bind("features", "heading", "Heading")} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, background: "#f8fafc", padding: "12px 14px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                <Field {...bind("features", "cta_label", "CTA Label")} />
                <Field {...bind("features", "cta_url", "CTA URL", { url: true })} />
              </div>
            </SubBlock>
            <Repeater
              title="Features"
              items={content.features.items}
              fields={[["title"], ["description", true]]}
              fieldRules={ITEM_RULES.feature}
              repeaterRules={REPEATER_RULES["features.items"]}
              path="features.items"
              onChange={(items) => setGroup("features", "items", items)}
              {...repeaterCommon}
            />
          </Section>

          {/* HOW PROCMAN WORKS */}
          <Section title="How Procman Works">
            <SubBlock title="Content">
              <Field {...bind("working", "heading", "Heading")} />
              {/* <Field textarea {...bind("working", "subheading", "Subheading")} /> */}
            </SubBlock>
            <Repeater
              title="Steps"
              items={content.working.steps}
              fields={[["title"], ["description", true]]}
              fieldRules={ITEM_RULES.step}
              repeaterRules={REPEATER_RULES["working.steps"]}
              path="working.steps"
              onChange={(steps) => setGroup("working", "steps", steps)}
              {...repeaterCommon}
            />
          </Section>

          {/* INDUSTRIES */}
          <Section title="Industries">
            <SubBlock title="Content">
              <Field {...bind("industries", "heading", "Heading")} />
            </SubBlock>
          </Section>

          {/* COMPARISON */}
          <Section title="Comparison">
            <SubBlock title="Content">
              <Field {...bind("comparison", "heading", "Heading")} />
              <Field textarea {...bind("comparison", "subheading", "Subheading")} />
            </SubBlock>
            <SubBlock title="Traditional Column">
              <Field {...bind("comparison", "left_title", "Left Title")} />
            </SubBlock>
            <StringListEditor
              title="Traditional Points"
              items={content.comparison.left_items}
              path="comparison.left_items"
              rules={TEXT_RULES["comparison.item"]}
              repeaterRules={REPEATER_RULES["comparison.left_items"]}
              onChange={(items) => setGroup("comparison", "left_items", items)}
              {...repeaterCommon}
            />
            <SubBlock title="Procman Column">
              <Field {...bind("comparison", "right_title", "Right Title")} />
            </SubBlock>
            <StringListEditor
              title="Procman Points"
              items={content.comparison.right_items}
              path="comparison.right_items"
              rules={TEXT_RULES["comparison.item"]}
              repeaterRules={REPEATER_RULES["comparison.right_items"]}
              onChange={(items) => setGroup("comparison", "right_items", items)}
              {...repeaterCommon}
            />
          </Section>

          {/* CASE STUDIES */}
          <Section title="Case Studies / Success Stories">
            <SubBlock title="Content">
              <Field {...bind("case_studies", "heading", "Heading")} />
              <Field textarea {...bind("case_studies", "subheading", "Subheading")} />
            </SubBlock>
          </Section>

          {/* TESTIMONIALS */}
          <Section title="Testimonials">
            <SubBlock title="Content">
              <Field {...bind("testimonials", "heading", "Heading")} />
              <Field textarea {...bind("testimonials", "subheading", "Subheading")} />
            </SubBlock>
          </Section>

          {/* FAQ */}
          <Section title="FAQ">
            <Repeater
              title="FAQs"
              items={content.faqs}
              fields={[["question"], ["answer", true]]}
              fieldRules={ITEM_RULES.faq}
              repeaterRules={REPEATER_RULES.faqs}
              path="faqs"
              onChange={(faqs) => setField("faqs", faqs)}
              {...repeaterCommon}
            />
          </Section>

          {/* CTA */}
          <Section title="CTA Section">
            <SubBlock title="Content">
              <Field {...bind("cta", "heading", "Heading")} />
              <Field textarea {...bind("cta", "subheading", "Subheading")} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, background: "#f8fafc", padding: "12px 14px", borderRadius: 8, border: "1px solid #e2e8f0", marginBottom: 8 }}>
                <Field {...bind("cta", "primary_cta_label", "Primary CTA Label")} />
                <Field {...bind("cta", "primary_cta_url", "Primary CTA URL", { url: true })} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, background: "#f8fafc", padding: "12px 14px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                <Field {...bind("cta", "secondary_cta_label", "Secondary CTA Label")} />
                <Field {...bind("cta", "secondary_cta_url", "Secondary CTA URL", { url: true })} />
              </div>
              <Repeater
                title="Benefits"
                items={content.cta.benefits || []}
                fields={[["text"]]}
                fieldRules={{ text: TEXT_RULES["cta.benefit.text"] }}
                repeaterRules={REPEATER_RULES["cta.benefits"]}
                path="cta.benefits"
                errors={errors}
                setErrors={setErrors}
                style={{ gridColumn: "span 2" }}
                onChange={(benefits) => setGroup("cta", "benefits", benefits)}
              />
            </SubBlock>
          </Section>


          {overlayUrl && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Image preview"
              onClick={() => setOverlayUrl("")}
              style={{ position: "fixed", inset: 0, zIndex: 1000, padding: 24, background: "rgba(15,23,42,.82)", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <img
                src={overlayUrl}
                alt="Full preview"
                onClick={(event) => event.stopPropagation()}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 10, background: "#fff" }}
              />
              <button
                type="button"
                onClick={() => setOverlayUrl("")}
                style={{ position: "fixed", top: 18, right: 18, margin: 0, border: "1px solid #fff", background: "rgba(15,23,42,.65)", color: "#fff", fontSize: 18, padding: "8px 12px" }}
              >
                ×
              </button>
            </div>
          )}
        </>

      )}
    </div>
  );
}